import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, Paper, Typography,
} from "@material-ui/core";
import {
  ArrowBack, AssignmentOutlined, AssignmentTurnedInOutlined, CheckCircleOutline,
  ConfirmationNumberOutlined, DateRangeOutlined, Refresh, VpnKeyOutlined,
} from "@material-ui/icons";
import { Alert, Skeleton } from "@material-ui/lab";
import axios from "services/auth/jwt/config";
import useListStyles from "../recordListStyles";
import { dateText, errorText } from "routes/Pages/FormTickets/utils";
import AnswerReview from "./AnswerReview";
import DetailRow from "./DetailRow";
import ParticipantDetails from "./ParticipantDetails";
import { isSummaryField, savedScore } from "./answerPresentation";
import useStyles from "./detailStyles";

export default function ResponseDetails({ id }) {
  const classes = useStyles();
  const list = useListStyles();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [success, setSuccess] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    axios.get(`/formfilled/getsingle/${id}`)
      .then(({ data }) => {
        if (!data || data._id !== id) throw new Error("ورودی مورد نظر یافت نشد");
        if (active) setRecord(data);
      })
      .catch(err => { if (active) setError(errorText(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, revision]);

  const form = record?.form && typeof record.form === "object" ? record.form : null;
  const formId = form?._id || (typeof record?.form === "string" ? record.form : null);
  const isExam = form?.type === "Exam";
  const qrTicket = form?.confirmation === "QRCode";
  const privacyMode = !form || Boolean(form.privacyMode);
  const name = privacyMode ? "شرکت‌کننده" : record?.owner?.fullName || "کاربر حذف شده";
  const personnel = record?.owner?.personnelCode;
  const displayedPersonnel = personnel == null ? "—" : privacyMode ? `**${String(personnel).slice(2)}` : personnel;
  const fields = record?.fields || [];
  const answers = fields.filter(item => !isExam || !isSummaryField(item));
  const score = isExam ? savedScore(fields) : null;

  const consume = async () => {
    setSaving(true);
    setActionError("");
    try {
      const { data } = await axios.get(`/formfilled/attend/admin/${id}`);
      if (!data.formfilled || data.formfilled._id !== id) throw new Error("تایید مصرف بلیط دریافت نشد؛ وضعیت را دوباره بارگذاری کنید");
      setRecord(current => ({ ...current, ...data.formfilled, form: current.form, owner: current.owner }));
      setConfirming(false);
      setSuccess("بلیط با موفقیت مصرف شد");
    } catch (err) { setActionError(errorText(err)); }
    finally { setSaving(false); }
  };

  return <>
    <Box className={list.hero}>
      <Box className={list.heroIdentity}>
        <Box className={list.heroIcon}><AssignmentTurnedInOutlined /></Box>
        <Box><Typography className={list.eyebrow}>جزئیات پاسخ ثبت‌شده</Typography>
          <Typography component="h2" className={list.heroTitle}>{form?.title || "جزئیات ورودی فرم"}</Typography></Box>
      </Box>
      <Box className={list.heroActions}>
        <Button component={Link} to={formId ? `/forminputs/${formId}?name=${encodeURIComponent(form?.title || "")}` : "/forms"} startIcon={<ArrowBack />} className={list.button}>{formId ? "بازگشت به ورودی‌ها" : "بازگشت به فرم‌ها"}</Button>
        {isExam && formId && <Button component={Link} to={`/formtickets/${formId}`} color="primary" variant="outlined" startIcon={<ConfirmationNumberOutlined />} className={list.button}>بلیط‌های آزمون</Button>}
        <Button disabled={loading || saving} startIcon={<Refresh />} className={list.button} onClick={() => setRevision(value => value + 1)}>بارگذاری مجدد</Button>
      </Box>
    </Box>
    {success && <Box mb={3}><Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert></Box>}
    {loading ? <Paper elevation={0} className={`${classes.panel} ${classes.loading}`}>
      <Typography role="status" color="textSecondary">در حال دریافت جزئیات پاسخ…</Typography>
      {[1, 2, 3].map(value => <Skeleton key={value} variant="rect" height={90} aria-hidden="true" />)}
    </Paper> : error ? <Alert severity="error">{error}</Alert> : record && <Grid container spacing={3} className={classes.layout}>
      <Grid item xs={12} md={8} className={classes.column}>
        <AnswerReview fields={answers} score={score} isExam={isExam} search={search} filter={filter} onSearchChange={setSearch} onFilterChange={setFilter} />
      </Grid>
      <Grid item xs={12} md={4} className={classes.column}>
        <ParticipantDetails owner={record.owner} privacyMode={privacyMode} />
        <Paper component="section" aria-label="جزئیات ورودی" elevation={0} className={classes.panel}>
          <Box className={classes.panelHeading}><Typography component="h3" className={classes.heading}><AssignmentOutlined />جزئیات ورودی</Typography></Box>
          <Box component="dl" className={classes.details}>
            <DetailRow label="شناسه ورودی" icon={<VpnKeyOutlined />}><Box component="code" className={classes.identifier}>{record._id}</Box></DetailRow>
            <DetailRow label="وضعیت" icon={<CheckCircleOutline />}><Chip size="small" label={qrTicket ? record.attended ? "مصرف شده" : "مصرف نشده" : "ثبت شده"} className={`${list.status} ${qrTicket && !record.attended ? list.inProgress : list.submitted}`} /></DetailRow>
            <DetailRow label="تاریخ ثبت پاسخ" icon={<DateRangeOutlined />}>{dateText(record.createdAt)}</DetailRow>
            <DetailRow label="آخرین به‌روزرسانی" icon={<DateRangeOutlined />}>{dateText(record.updatedAt)}</DetailRow>
          </Box>
          {qrTicket && !record.attended && <Box className={classes.consumeAction}><Button fullWidth color="primary" variant="contained" startIcon={<ConfirmationNumberOutlined />} className={list.button}
            onClick={() => { setActionError(""); setConfirming(true); }}>مصرف بلیط</Button></Box>}
        </Paper>
      </Grid>
    </Grid>}
    <Dialog open={confirming} onClose={() => { if (!saving) setConfirming(false); }} fullWidth maxWidth="sm" aria-labelledby="consume-response-title" PaperProps={{ className: list.dialogPaper }}>
      <DialogTitle id="consume-response-title" className={list.dialogTitle}>ثبت مصرف بلیط</DialogTitle>
      <DialogContent>
        <Box className={list.dialogParticipant}><Avatar className={list.avatar}>{name.trim().charAt(0)}</Avatar><Box><Typography className={list.participantName}>{name}</Typography>
          <Typography className={list.secondary}>کد پرسنلی: {displayedPersonnel}</Typography></Box></Box>
        <Typography className={list.dialogDescription}>آیا از مصرف بلیط این ورودی اطمینان دارید؟ پس از تایید، وضعیت بلیط «مصرف شده» ثبت می‌شود.</Typography>
        {actionError && <Alert severity="error">{actionError}</Alert>}
      </DialogContent>
      <DialogActions className={list.dialogActions}><Button disabled={saving} onClick={() => setConfirming(false)} className={list.button}>انصراف</Button>
        <Button disabled={saving} color="primary" variant="contained" onClick={consume} className={list.button}>{saving ? "در حال ثبت…" : "تایید مصرف"}</Button></DialogActions>
    </Dialog>
  </>;
}
